import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { StyledStickyHeader, Wrapper } from './Common';

type ErrorContentProps = {
    readonly error: unknown;
    readonly onClose: () => void;
};

const Headline = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const Title = styled.h1(({ theme }) => ({
    ...theme.typography.headline.h4.regular,
    color: theme.colors.black[900],
    margin: 0,
}));

const Message = styled.p(({ theme }) => ({
    ...theme.typography.body.S.regular,
    color: theme.colors.gray[900],
    margin: 0,
}));

export const ErrorContent: FunctionComponent<ErrorContentProps> = ({ error, onClose }) => {
    const { translate } = useContext(ApplicationContext);

    if (!is.object(error) || !('message' in error) || !('errorCode' in error)) {
        return (
            <Wrapper data-testid="error-content">
                <StyledStickyHeader>
                    <SidepanelHeader
                        actions={undefined}
                        activeLanguage={undefined}
                        disableArrows={true}
                        isGenerating={false}
                        isLanguagePickerDisabled={true}
                        languages={[]}
                        setActiveLanguage={noop}
                        onClose={onClose}
                    />
                </StyledStickyHeader>
                <Headline>
                    <Title>{translate('errors.status.unknown.title')}</Title>
                    <Message>{translate('errors.status.unknown.message')}</Message>
                </Headline>
            </Wrapper>
        );
    }

    if (error.errorCode === 'JSA0001') {
        return (
            <Wrapper data-testid="error-content">
                <StyledStickyHeader>
                    <SidepanelHeader
                        actions={undefined}
                        activeLanguage={undefined}
                        disableArrows={true}
                        isGenerating={false}
                        isLanguagePickerDisabled={true}
                        languages={[]}
                        setActiveLanguage={noop}
                        onClose={onClose}
                    />
                </StyledStickyHeader>
                <Headline>
                    <Title>{translate('errors.status.404.title')}</Title>
                    <Message>{translate('errors.status.404.message')}</Message>
                </Headline>
            </Wrapper>
        );
    }

    const errorMsg = is.string(error.message) ? error.message : '';

    return (
        <Wrapper data-testid="error-content">
            <StyledStickyHeader>
                <SidepanelHeader
                    actions={undefined}
                    activeLanguage={undefined}
                    disableArrows={true}
                    isGenerating={false}
                    isLanguagePickerDisabled={true}
                    languages={[]}
                    setActiveLanguage={noop}
                    onClose={onClose}
                />
            </StyledStickyHeader>
            <Headline>
                <Title>{translate('pages.job-architecture-v2.job-profile-error-prefix')}</Title>
                <Message>{errorMsg}</Message>
            </Headline>
        </Wrapper>
    );
};
